/**
 * Ankündigung erfassen – der Anruf VOR dem Versand.
 *
 * Wir rufen den Kunden an, bevor das Angebot hinausgeht: Er soll wissen, dass
 * gerechnet wurde, und die Mail nicht ungelesen im Posteingang liegen lassen.
 * Das ist ein Gespräch mit demselben Kunden wie die Rückmeldung danach und
 * steht in derselben Chronik – nur die Frage ist eine andere: Haben wir ihn
 * erreicht?
 *
 * Erreicht heißt versandfertig. Nicht erreicht heißt: ein Versuch mehr, und
 * der Vorgang nennt in „Meine Aufgaben" den nächsten Termin. Der Versand
 * selbst wird hier nicht ausgelöst – er läuft über „Status ändern".
 */

import { useState } from 'react';
import { useMutation } from '@tanstack/react-query';
import { api } from '../api';
import { useKora, useMeta } from '../zustand';
import { Modal, KnopfPrimaer, KnopfSekundaer } from '../komponenten/Modal';
import { dat, jetztUhrzeit } from '../hilfen/format';
import type { Angebot, AngebotAntwort } from '../typen';

/** Termin plus n Tage, gerechnet vom Serverdatum – nicht von der Systemuhr. */
function plusTage(iso: string, tage: number): string {
  const d = new Date(iso + 'T12:00:00');
  d.setDate(d.getDate() + tage);
  return d.toISOString().slice(0, 10);
}

export function AnkuendigungModal({
  angebot,
  onSchliessen,
  onFertig,
}: {
  angebot: Angebot;
  onSchliessen: () => void;
  onFertig: () => void;
}) {
  const { toastZeigen } = useKora();
  const meta = useMeta();

  const [datum, setDatum] = useState(meta.heute);
  const [zeit, setZeit] = useState(jetztUhrzeit());
  const [erreicht, setErreicht] = useState(true);
  const [notiz, setNotiz] = useState('');
  /* Ein Tag Abstand ist der Regelfall – wer heute nicht abhebt, hebt morgen ab. */
  const [erneutAm, setErneutAm] = useState(() => plusTage(meta.heute, 1));
  const [fehler, setFehler] = useState('');

  const nummer =
    angebot.kunde.telefon || angebot.kunde.mobil || angebot.kunde.mobilGeschaeftlich || '';

  const mutation = useMutation({
    mutationFn: () =>
      api.sende<AngebotAntwort>(`/angebote/${angebot.id}/ankuendigung`, {
        datum,
        zeit,
        erreicht,
        notiz: notiz.trim(),
        erneutAm: erreicht ? null : erneutAm,
      }),
    onSuccess: (antwort) => {
      onFertig();
      if (antwort.toast) toastZeigen(antwort.toast);
      onSchliessen();
    },
    onError: (f) => setFehler(f instanceof Error ? f.message : 'Speichern fehlgeschlagen.'),
  });

  function speichern() {
    if (!notiz.trim()) {
      setFehler(
        erreicht
          ? 'Bitte kurz festhalten, was besprochen wurde.'
          : 'Bitte kurz festhalten, was versucht wurde – etwa „Mobilbox, keine Nachricht".',
      );
      return;
    }
    if (!erreicht && !erneutAm) {
      setFehler('Bitte angeben, wann der nächste Versuch sein soll.');
      return;
    }
    mutation.mutate();
  }

  const versuche = angebot.versand.versuche;

  return (
    <Modal
      titel="Ankündigung erfassen"
      untertitel={`${angebot.name} · ${angebot.kunde.name} · ${nummer || 'keine Nummer hinterlegt'}`}
      breite={520}
      fest={mutation.isPending}
      onSchliessen={onSchliessen}
      fuss={
        <>
          <KnopfSekundaer onClick={onSchliessen} disabled={mutation.isPending}>
            Abbrechen
          </KnopfSekundaer>
          <KnopfPrimaer onClick={speichern} disabled={mutation.isPending}>
            {mutation.isPending ? 'Wird gespeichert …' : 'Speichern'}
          </KnopfPrimaer>
        </>
      }
    >
      {versuche > 0 && (
        <div className="modal-hinweis">
          Bisher {versuche === 1 ? 'ein Versuch' : `${versuche} Versuche`} ohne Erfolg
          {angebot.versand.erneutAm ? ` · vorgemerkt für den ${dat(angebot.versand.erneutAm)}` : ''}.
        </div>
      )}

      <div style={{ display: 'flex', gap: 12, flexWrap: 'wrap' }}>
        <label className="modal-feld" style={{ flex: 1, minWidth: 150 }}>
          <span>Datum</span>
          <input
            type="date"
            className="modal-eingabe"
            value={datum}
            onChange={(e) => {
              setDatum(e.target.value);
              setFehler('');
            }}
          />
        </label>
        <label className="modal-feld" style={{ width: 130 }}>
          <span>Uhrzeit</span>
          <input
            type="time"
            className="modal-eingabe"
            value={zeit}
            onChange={(e) => {
              setZeit(e.target.value);
              setFehler('');
            }}
          />
        </label>
      </div>

      {/* Die eine Frage, die zählt. Alles Weitere ist Notiz. */}
      <div className="modal-feld">
        <span>Kunde erreicht?</span>
        <div className="eh-chips">
          <button
            type="button"
            className={`modal-radio ${erreicht ? 'aktiv' : ''}`}
            onClick={() => {
              setErreicht(true);
              setFehler('');
            }}
          >
            Ja, Angebot angekündigt
          </button>
          <button
            type="button"
            className={`modal-radio ${!erreicht ? 'aktiv' : ''}`}
            onClick={() => {
              setErreicht(false);
              setFehler('');
            }}
          >
            Nicht erreicht
          </button>
        </div>
      </div>

      <label className="modal-feld">
        <span>Gesprächsnotiz</span>
        <textarea
          className="modal-textarea"
          rows={3}
          placeholder={
            erreicht
              ? 'Was wurde besprochen? Wann erwartet der Kunde das Angebot?'
              : 'z. B. Mobilbox, keine Nachricht hinterlassen'
          }
          value={notiz}
          onChange={(e) => {
            setNotiz(e.target.value);
            setFehler('');
          }}
        />
      </label>

      {!erreicht && (
        <div className="modal-feld">
          <span>Nächster Versuch am</span>
          <div className="modal-termin">
            <input
              type="date"
              className={`modal-eingabe ${fehler && !erneutAm ? 'modal-eingabe--fehler' : ''}`}
              value={erneutAm}
              min={meta.heute}
              onChange={(e) => {
                setErneutAm(e.target.value);
                setFehler('');
              }}
            />
            <div className="eh-chips">
              {[
                { label: 'morgen', tage: 1 },
                { label: '+2 Tage', tage: 2 },
                { label: '+1 Woche', tage: 7 },
              ].map((s) => (
                <button
                  key={s.tage}
                  type="button"
                  className={`modal-radio ${erneutAm === plusTage(meta.heute, s.tage) ? 'aktiv' : ''}`}
                  onClick={() => {
                    setErneutAm(plusTage(meta.heute, s.tage));
                    setFehler('');
                  }}
                >
                  {s.label}
                </button>
              ))}
            </div>
          </div>
        </div>
      )}

      <div className="modal-hinweis">
        {erreicht
          ? 'Das Angebot gilt als angekündigt und steht unter „Zu versenden" als versandfertig. Der Versand selbst wird über „Status ändern → Gesendet" erfasst.'
          : 'Der Vorgang bleibt unter „Zu versenden", zählt einen Versuch mehr und nennt den nächsten Termin.'}
      </div>

      {fehler && <div className="modal-fehler">{fehler}</div>}
    </Modal>
  );
}
